import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useEffect, useState } from 'react';
import { ProductRecommendationTypes, RelevanceModifier } from '../services/RecommendationService';
import { useRecommendationService } from './serviceHooks';
import { DisplayedAtLocation, MappedProduct } from '../types/mapped';
import { Gender } from '../../../shared/services/gender-cookie-service/gender-cookie-service.types';

const DEFAULT_PRODUCT_COUNT = 6;
const MAX_PRODUCT_COUNT = 20;

interface UseProductRecommendationOptions {
    productCount: number;
    recommendationType: ProductRecommendationTypes;
    isEnabled: boolean;
    displayLocation: DisplayedAtLocation;
    categoryRecordId?: number;
    masterProductItemId?: string;
    gender?: Gender;
    relevanceModifier?: RelevanceModifier;
    relevanceModifierStrength?: number;
    request: IRequestContext;
    telemetry: ITelemetry;
}

export const useProductRecommendations = ({
    isEnabled,
    request,
    telemetry,
    productCount,
    displayLocation,
    recommendationType,
    categoryRecordId,
    masterProductItemId,
    gender,
    relevanceModifier,
    relevanceModifierStrength
}: UseProductRecommendationOptions) => {
    const [products, setProducts] = useState<MappedProduct[]>([]);
    const [isLoading, setIsLoading] = useState(isEnabled);

    const relewiseRecommendationService = useRecommendationService(request, telemetry);

    useEffect(
        function loadRecommendations() {
            if (!isEnabled) {
                return;
            }

            setIsLoading(true);

            const fetchRecommendations = async (): Promise<void> => {
                const numberOfProducts = Math.min(productCount ?? DEFAULT_PRODUCT_COUNT, MAX_PRODUCT_COUNT);
                const result = await relewiseRecommendationService.getProductRecommendations({
                    recommendationType,
                    displayLocation,
                    categoryRecordId,
                    masterProductItemId,
                    gender,
                    relevanceModifier,
                    relevanceModifierStrength,
                    count: numberOfProducts
                });

                setProducts(result ?? []);
                setIsLoading(false);
            };

            fetchRecommendations();
        },
        [
            recommendationType,
            displayLocation,
            categoryRecordId,
            masterProductItemId,
            gender,
            relevanceModifier,
            relevanceModifierStrength,
            productCount,
            isEnabled,
            relewiseRecommendationService
        ]
    );

    return { products, isLoading };
};
